/**
 * Cultural / pragmatic note badge — small icon collapsed by default;
 * expands on click. Used when a Situation card has `pragmaticNote` or
 * `notes` populated.
 *
 * No external icon library — uses an emoji glyph (💡) to keep the bundle
 * lean. Matches the JLPT / English app's emoji-as-glyph convention.
 */

import { useState } from 'preact/hooks';

interface NoteContent {
  en?: string;
  ja?: string;
}

interface Props {
  /** Surfaced as a 💡 cultural-mismatch callout. Highest priority. */
  pragmaticNote?: NoteContent;
  /** Generic usage notes — secondary, rendered with a 📝 glyph. */
  notes?: NoteContent;
}

export function CulturalNoteBadge({ pragmaticNote, notes }: Props) {
  const [expanded, setExpanded] = useState(false);

  const hasPragmatic = Boolean(pragmaticNote?.en || pragmaticNote?.ja);
  const hasNotes = Boolean(notes?.en || notes?.ja);
  if (!hasPragmatic && !hasNotes) return null;

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition-all"
        aria-expanded={expanded}
      >
        <span aria-hidden="true">{hasPragmatic ? '💡' : '📝'}</span>
        <span>
          {hasPragmatic ? 'Cultural note' : 'Usage note'}
          {' '}
          {expanded ? '▾' : '▸'}
        </span>
      </button>

      {expanded && (
        <div className="mt-2 px-3 py-2 rounded-xl bg-amber-50/50 border border-amber-100 text-sm space-y-2">
          {pragmaticNote?.en && (
            <p className="text-amber-900 leading-snug">{pragmaticNote.en}</p>
          )}
          {pragmaticNote?.ja && (
            <p className="text-amber-900 font-japanese tracking-japanese leading-snug">
              {pragmaticNote.ja}
            </p>
          )}
          {notes?.en && (
            <p className="text-slate-700 leading-snug border-t border-amber-100 pt-2 first:border-0 first:pt-0">
              {notes.en}
            </p>
          )}
          {notes?.ja && (
            <p className="text-slate-700 font-japanese tracking-japanese leading-snug">
              {notes.ja}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
